import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { ShieldCheck, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export default function ActivatePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const token = new URLSearchParams(location.search).get('token') || '';
  const [state, setState] = useState('checking'); // checking | valid | invalid | done
  const [info, setInfo] = useState(null);
  const [reason, setReason] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!token) { setState('invalid'); setReason('No invitation token provided.'); return; }
    axios.get(`${API}/api/portal/invitation/${token}`).then((r) => {
      if (r.data.valid) { setInfo(r.data); setState('valid'); }
      else { setReason(r.data.reason || 'Invalid link.'); setState('invalid'); }
    }).catch(() => { setReason('Could not validate this invitation.'); setState('invalid'); });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault(); setErr('');
    if (pw.length < 6) { setErr('Password must be at least 6 characters.'); return; }
    if (pw !== pw2) { setErr('Passwords do not match.'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/api/portal/activate`, { token, password: pw });
      setState('done');
    } catch (e2) { setErr(e2?.response?.data?.detail || 'Activation failed.'); }
    finally { setBusy(false); }
  };

  const Shell = ({ children }) => (
    <div className="min-h-screen bg-[#0C0E11] flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-[#14171B] border border-white/[0.08] rounded-2xl p-8" data-testid="activate-page">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-9 h-9 rounded-lg bg-[#C6A15B]/15 flex items-center justify-center"><ShieldCheck className="w-5 h-5 text-[#C6A15B]" /></div>
          <div><p className="text-white font-semibold leading-tight">TC Protection Film</p><p className="text-slate-500 text-xs">Portal Activation</p></div>
        </div>
        {children}
      </div>
    </div>
  );

  if (state === 'checking') return <Shell><div className="flex items-center gap-2 text-slate-400"><Loader2 className="w-4 h-4 animate-spin" /> Validating your invitation…</div></Shell>;
  if (state === 'invalid') return <Shell><div className="text-center py-4"><AlertTriangle className="w-10 h-10 text-amber-400 mx-auto mb-3" /><p className="text-white font-medium mb-1">Invitation not valid</p><p className="text-slate-400 text-sm">{reason}</p></div></Shell>;
  if (state === 'done') return <Shell><div className="text-center py-4"><CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-3" /><p className="text-white font-medium mb-1">Portal access activated</p><p className="text-slate-400 text-sm mb-5">You can now sign in with your username and new password.</p><button onClick={() => navigate('/login')} data-testid="go-to-login" className="w-full bg-[#C6A15B] hover:bg-[#d8b36c] text-black font-medium text-sm px-4 py-2.5 rounded-xl">Go to Sign In</button></div></Shell>;

  const cls = "w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-slate-100 focus:border-[#C6A15B]/60 focus:outline-none";
  return (
    <Shell>
      <p className="text-slate-300 text-sm mb-1">Welcome{info?.full_name ? `, ${info.full_name}` : ''}.</p>
      <p className="text-slate-500 text-xs mb-5">Set a password to activate portal access for <b className="text-slate-300">{info?.company_name || info?.email}</b>. Your username is <b className="text-[#C6A15B]">{info?.username}</b>.</p>
      <form onSubmit={submit} className="space-y-3">
        <div><label className="block text-xs text-slate-400 mb-1">New Password</label><input type="password" className={cls} value={pw} onChange={(e) => setPw(e.target.value)} data-testid="activate-password" placeholder="At least 6 characters" /></div>
        <div><label className="block text-xs text-slate-400 mb-1">Confirm Password</label><input type="password" className={cls} value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="activate-password-confirm" /></div>
        {err && <p className="text-red-400 text-xs" data-testid="activate-error">{err}</p>}
        <button type="submit" disabled={busy} data-testid="activate-submit" className="w-full inline-flex items-center justify-center gap-2 bg-[#C6A15B] hover:bg-[#d8b36c] text-black font-medium text-sm px-4 py-2.5 rounded-xl disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Activate Portal Access
        </button>
      </form>
    </Shell>
  );
}
